import { defineComponent } from "vue";
import { loadLogin, parseAccessToken, validateCredentials } from "@/store/login/actions";
import { ISdkLoginCredentials, ISdkLoginResponse } from "@/interfaces/sdk-login";
import { SdkCompany } from "@/interfaces/sdk-company";
import { SdkEnvironment } from "@/interfaces/sdk-environment";
import { loadAllEnvironment, loadEnvironmentByCompanyAndUser } from "@/store/environment/actions";
import { ETypeNotification, INotification } from "@/interfaces/i-notification";
import { EventBus } from "@/utils/event-bus";
import { findVersionInfoSdk } from "@/store/version/actions";
import { getCookie, setCookie } from "@/utils/cookie";

export default defineComponent({
  name: "sdk-login",
  data() {
    return {
      user: "",
      password: "",
      formData: { company: { name: "", id: undefined, cnpj: "" }, environment: { name: "", id: undefined } },
      companies: [] as SdkCompany[],
      environments: [] as SdkEnvironment[],
      userWorkspace: false,
      loginData: <ISdkLoginCredentials>{},
      notification: [] as INotification[],
      versionSdk: "",
      expiresCookie: "",
      loading: false,
      loadingEnvs: false,
      loadingCompanies: false,
    };
  },
  computed: {
    blockLogin(): boolean {
      if (!this.userWorkspace) {
        return !this.user || !this.password;
      } else {
        return !this.formData.company.id || !this.formData.environment.id;
      }
    },
  },
  watch: {
    $route: "updateLogin",
  },
  methods: {
    async goToWorkspace() {
      this.loading = true;
      this.loginData = { username: this.user, password: this.password };
      await this.$dispatch(validateCredentials)([this.loginData])
        .then((res: any) => {
          if (!res.valid) {
            const notification = {
              type: ETypeNotification.DANGER,
              title: this.$t("pages.loginPage.notifications.title.error"),
              message: this.$t("pages.loginPage.notifications.invalidCredentials"),
            };
            EventBus.$emit("emmit-notification", notification);
            this.loading = false;
            return;
          }
          this.companies = res.companies;
          this.userWorkspace = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async fetchEnvironments() {
      await this.$dispatch(loadAllEnvironment)().then((data: SdkEnvironment[]) => {
        this.environments = data;
      });
    },
    async fetchEnvironmentByCompany() {
      this.loadingEnvs = true;
      await this.$dispatch(loadEnvironmentByCompanyAndUser)([this.formData.company.id, this.user.toUpperCase()]).then((data: SdkEnvironment[]) => {
        this.environments = data;
      });
      this.loadingEnvs = false;
    },
    async fetchVersionSdk() {
      await this.$dispatch(findVersionInfoSdk)().then((res: any) => {
        this.versionSdk = res.app.version;
      });
    },
    updateLogin() {
      if (this.$route.name === "logout") {
        this.deleteAllCookies();
        document.cookie = "access_token" + "=; Max-Age=0; path=/;";
      }
    },
    setCompanyInfo() {
      const company = this.companies.find((comp: SdkCompany) => comp.id === this.formData.company.id) as SdkCompany;
      setCookie("companyName", company.name);
    },
    deleteAllCookies() {
      const cookies = document.cookie.split(";");
      // tslint:disable-next-line:prefer-for-of
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;";
      }
    },
    async login() {
      this.loading = true;
      const d = new Date();
      d.setTime(d.getTime() + 2 * 24 * 60 * 60 * 1000);
      const expires = "expires=" + d.toUTCString();
      this.expiresCookie = expires;
      await this.$dispatch(loadLogin)([this.loginData, this.formData.environment.id])
        .then((res: ISdkLoginResponse) => {
          document.cookie = "access_token =" + res.access_token + "; expires=" + expires + ";path=/";
          this.setCompanyInfo();
          this.$store.dispatch(parseAccessToken());
          this.$router.push({ name: "dashboard" });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    goToHome() {
      if (!this.formData.company.id) {
        this.notification.push({
          type: ETypeNotification.INFO,
          title: this.$t("pages.loginPage.notifications.title.warning") as string,
          message: this.$t("pages.loginPage.notifications.selectCompany") as string,
        });
        EventBus.$emit("emmit-notification", this.notification[0]);
        this.notification = [];
      } else if (!this.formData.environment.id) {
        this.notification.push({
          type: ETypeNotification.INFO,
          title: this.$t("pages.loginPage.notifications.title.warning") as string,
          message: this.$t("pages.loginPage.notifications.selectEnvironment") as string,
        });
        EventBus.$emit("emmit-notification", this.notification[0]);
        this.notification = [];
      } else {
        this.login();
      }
    },
  },
  beforeCreate() {
    const cookies = document.cookie.split(";");
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;";
    }
  },
  async mounted() {
    const language = getCookie("language");
    if (!language) {
      this.$i18n.locale = `${navigator.language.substring(0, 2)}_${navigator.language.substring(3, 5)}`;
      document.cookie = `language = ${this.$i18n.locale}`;
    }
    await this.fetchVersionSdk();
    this.deleteAllCookies();
  },
});
